// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@sc/App/Components/Common/XngZone";
import LazyPicture from "@sc/MobiusOverrides/LazyImage/LazyPicture";
import React, { FC } from "react";

const enum fields {
    backgroundImage = "backgroundImage",
    desktopBackgroundImage = "desktopBackgroundImage",
    mediumBackgroundImage = "mediumBackgroundImage",
    smallBackgroundImage = "smallBackgroundImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundImage]: string;
        [fields.desktopBackgroundImage]: string;
        [fields.mediumBackgroundImage]: string;
        [fields.smallBackgroundImage]: string;
    };
}

type Props = WidgetProps & OwnProps;

export interface SignInPageLayoutStyles {
    container?: InjectableCss;
    pictureWrapper?: InjectableCss;
    logoSection?: InjectableCss;
    notifySection?: InjectableCss;
    signInSection?: InjectableCss;
}

export const signInPageLayout: SignInPageLayoutStyles = {};

const styles = signInPageLayout;
const SignInPageLayout: FC<Props> = ({ id, fields }) => {
    return (
        <>
            <StyledWrapper {...styles.container} data-test-selector="SignInPageLayout">
                <StyledWrapper {...styles.pictureWrapper}>
                    <LazyPicture
                        largeImageSrc={fields.desktopBackgroundImage || fields.backgroundImage}
                        mediumImageSrc={
                            fields.mediumBackgroundImage ||
                            fields.backgroundImage ||
                            fields.desktopBackgroundImage ||
                            fields.smallBackgroundImage
                        }
                        smallImageSrc={
                            fields.smallBackgroundImage ||
                            fields.backgroundImage ||
                            fields.desktopBackgroundImage ||
                            fields.mediumBackgroundImage
                        }
                        altText="signIn-Page-image"
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.logoSection}>
                    <XngZone contentId={id} zoneName="Content00" hint="Logo" />
                </StyledWrapper>
                <StyledWrapper {...styles.notifySection}>
                    <XngZone contentId={id} zoneName="Content01" hint="Notify" />
                </StyledWrapper>
                <StyledWrapper {...styles.signInSection}>
                    <XngZone contentId={id} zoneName="Content02" hint="SignInExistingAccount" />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: SignInPageLayout,
    definition: {
        group: "Layout" as any,
        displayName: "SignIn page layout",
        allowedContexts: ["SignInPage"],
        fieldDefinitions: [
            {
                name: fields.backgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Large Background Image",
            },
            {
                name: fields.desktopBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Desktop Background Image",
            },
            {
                name: fields.mediumBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Medium Background Image",
            },
            {
                name: fields.smallBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Small Background Image",
            },
        ],
    },
};

export default widgetModule;
